import React, { useState } from "react";
import {
  Box,
  Button,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  SvgIcon,
  Typography,
} from "@mui/material";
import { useLocalization } from "../localization/LocalizationContext";
import { viewerSubtleIconButtonSx } from "./unityWebViewer/panelStyles";

const LanguageIcon = (props) => (
  <SvgIcon viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm6.92 6h-2.95a15.7 15.7 0 0 0-1.38-3.56A8.06 8.06 0 0 1 18.92 8ZM12 4c.83 1.2 1.47 2.54 1.82 4h-3.64A13.35 13.35 0 0 1 12 4ZM4.26 14a7.8 7.8 0 0 1 0-4h3.39a16.5 16.5 0 0 0 0 4H4.26Zm.82 2h2.95c.3 1.28.77 2.48 1.38 3.56A8.06 8.06 0 0 1 5.08 16Zm2.95-8H5.08a8.06 8.06 0 0 1 4.33-3.56A15.7 15.7 0 0 0 8.03 8ZM12 20a13.35 13.35 0 0 1-1.82-4h3.64A13.35 13.35 0 0 1 12 20Zm2.2-6H9.8a14.5 14.5 0 0 1 0-4h4.4a14.5 14.5 0 0 1 0 4Zm.39 5.56A15.7 15.7 0 0 0 15.97 16h2.95a8.06 8.06 0 0 1-4.33 3.56ZM16.35 14a16.5 16.5 0 0 0 0-4h3.39a7.8 7.8 0 0 1 0 4h-3.39Z" />
  </SvgIcon>
);

const menuPaperSx = {
  mt: 1,
  width: 238,
  maxWidth: "calc(100vw - 24px)",
  maxHeight: "min(430px, calc(100vh - 88px))",
  color: "rgba(242, 255, 252, 0.92)",
  background:
    "linear-gradient(180deg, rgba(15, 27, 34, 0.98) 0%, rgba(7, 17, 23, 0.99) 100%)",
  border: "1px solid rgba(210, 255, 249, 0.12)",
  borderRadius: "12px",
  boxShadow: "0 18px 46px rgba(0, 0, 0, 0.42)",
  backdropFilter: "blur(18px)",
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(161, 248, 241, 0.28) transparent",
  "&::-webkit-scrollbar": {
    width: 6,
  },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(161, 248, 241, 0.28)",
    borderRadius: 999,
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "& .MuiMenu-list": {
    p: 0.75,
  },
};

const menuItemSx = {
  minHeight: 42,
  px: 1.25,
  borderRadius: "8px",
  gap: 1.1,
  color: "rgba(242, 255, 252, 0.82)",
  "&:hover": {
    background: "rgba(161, 248, 241, 0.08)",
  },
  "&.Mui-selected": {
    color: "#a1f8f1",
    background: "rgba(161, 248, 241, 0.12)",
  },
  "&.Mui-selected:hover": {
    background: "rgba(161, 248, 241, 0.17)",
  },
};

const LanguagePicker = ({ confirmReload = false, onReload = () => window.location.reload(), open, onOpen, onClose, sx }) => {
  const { dir, locale, setLocale, supportedLocales, t } = useLocalization();
  const [anchorElement, setAnchorElement] = useState(null);
  const [pendingLocale, setPendingLocale] = useState(null);
  const menuOpen = open ?? Boolean(anchorElement);

  const closeMenu = () => {
    setAnchorElement(null);
    onClose?.();
  };

  const selectLocale = (nextLocale) => {
    closeMenu();
    if (nextLocale === locale) return;
    if (confirmReload) {
      setPendingLocale(nextLocale);
      return;
    }
    setLocale(nextLocale);
  };

  const confirm = () => {
    if (!pendingLocale) return;
    setLocale(pendingLocale);
    onReload();
  };

  return (
    <ClickAwayListener onClickAway={closeMenu}>
      <Box component="span" sx={{ display: "contents" }}>
      <IconButton
        aria-label={t("language_picker.open")}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onClick={(event) => {
          if (menuOpen) {
            closeMenu();
          } else {
            setAnchorElement(event.currentTarget);
            onOpen?.();
          }
        }}
        size="small"
        sx={{
          ...viewerSubtleIconButtonSx,
          flexShrink: 0,
          color: menuOpen
            ? "rgba(242, 255, 252, 0.9)"
            : viewerSubtleIconButtonSx.color,
          background: menuOpen
            ? "rgba(210, 255, 249, 0.1)"
            : viewerSubtleIconButtonSx.background,
          opacity: menuOpen ? 1 : viewerSubtleIconButtonSx.opacity,
          "& .MuiSvgIcon-root": { fontSize: 17 },
          ...sx,
        }}
      >
        <LanguageIcon />
      </IconButton>
      <Menu
        anchorEl={anchorElement}
        open={menuOpen}
        onClose={closeMenu}
        // The viewer header stays interactive above this menu so its panels can switch.
        disableEnforceFocus={open !== undefined}
        disableRestoreFocus={open !== undefined && !menuOpen}
        anchorOrigin={{ vertical: "bottom", horizontal: dir === "rtl" ? "left" : "right" }}
        transformOrigin={{ vertical: "top", horizontal: dir === "rtl" ? "left" : "right" }}
        MenuListProps={{
          "aria-label": t("language_picker.title"),
        }}
        PaperProps={{ sx: menuPaperSx }}
      >
        <Box
          component="li"
          role="presentation"
          sx={{ display: "flex", alignItems: "center", minHeight: 42, px: 1.25, mb: 0.35 }}
        >
          <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "rgba(242, 255, 252, 0.78)" }}>
            {t("language_picker.title")}
          </Typography>
        </Box>
        {supportedLocales.map((option) => (
          <MenuItem
            key={option.locale}
            autoFocus={option.locale === locale}
            selected={option.locale === locale}
            onClick={() => selectLocale(option.locale)}
            dir={dir}
            sx={menuItemSx}
          >
            <ListItemText
              primary={option.nativeName}
              secondary={option.nativeName === option.englishName ? undefined : option.englishName}
              sx={{ textAlign: dir === "rtl" ? "right" : "left" }}
              primaryTypographyProps={{
                component: "span",
                dir: option.isRtl ? "rtl" : "ltr",
                fontSize: 13,
                fontWeight: option.locale === locale ? 750 : 600,
                textAlign: dir === "rtl" ? "right" : "left",
              }}
              secondaryTypographyProps={{
                component: "span",
                dir: "ltr",
                fontSize: 10.5,
                color: "rgba(210, 255, 249, 0.46)",
                textAlign: dir === "rtl" ? "right" : "left",
              }}
            />
          </MenuItem>
        ))}
      </Menu>
      <Dialog
        open={Boolean(pendingLocale)}
        onClose={() => setPendingLocale(null)}
        PaperProps={{
          sx: {
            maxWidth: 420,
            color: "#f2fffc",
            background: "linear-gradient(180deg, #102029 0%, #081218 100%)",
            border: "1px solid rgba(210, 255, 249, 0.12)",
            borderRadius: "14px",
            boxShadow: "0 24px 64px rgba(0, 0, 0, 0.52)",
          },
        }}
      >
        <DialogTitle sx={{ fontSize: "1.08rem", fontWeight: 800 }}>
          {t("language_picker.reload.title")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "rgba(242, 255, 252, 0.68)" }}>
            {t("language_picker.reload.body")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingLocale(null)} sx={{ color: "rgba(210, 255, 249, 0.72)" }}>
            {t("common.not_now")}
          </Button>
          <Button onClick={confirm} variant="contained">
            {t("language_picker.reload.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </ClickAwayListener>
  );
};

export default LanguagePicker;
